import { ft } from '../core/units.js';

/**
 * What the player is close enough to, and facing enough, to press E at.
 *
 * Proximity and facing rather than a camera ray. A ray is precise and wrong for
 * this game: the camera is over the shoulder, so the crosshair a ray implies is
 * not where the player is standing, and reaching for a wall-mounted time clock
 * becomes an aiming exercise. What the player means by "use the time clock" is
 * "I am at the time clock, looking at it roughly", and that is what this
 * measures.
 *
 * Candidates are scored, not just filtered, because the muster alcove has the
 * clock on one wall and the BEO board on the other and you will often be inside
 * both reaches at once. Closest to the *centre of view* wins, so turning your
 * head is how you choose.
 */

/** How far off-axis a target can be and still count. cos(60 degrees). */
const FACING_THRESHOLD = 0.5;

export function createInteraction({ level, interactables }) {
  // Resolved once: props do not move, so the geometry of this never changes.
  const targets = interactables
    .map((entry) => {
      const prop = level.level.props.find((candidate) => candidate.id === entry.prop);
      if (!prop) {
        throw new Error(`interactable "${entry.id}" points at prop "${entry.prop}", which is not in the level`);
      }
      const [x0, z0, x1, z1] = prop.rect;
      const base = prop.base ?? 0;
      return {
        ...entry,
        prop,
        centre: {
          x: ft((x0 + x1) / 2),
          y: ft(base + prop.height / 2),
          z: ft((z0 + z1) / 2),
        },
        // Reach is measured from the prop's edge, so a long shelf is usable
        // along its whole length rather than only opposite its middle.
        half: { x: ft(Math.abs(x1 - x0) / 2), z: ft(Math.abs(z1 - z0) / 2) },
        reachMetres: ft(entry.reach),
      };
    });

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  let current = null;
  /** Set by the game: an id here is offered but not usable, with a reason. */
  const gates = new Map();
  /**
   * Targets that should not be offered at all right now.
   *
   * Distinct from a gate, which says "here but not for you". A department's
   * station is a piece of furniture until you are short of what it holds, and a
   * prompt that says "nothing you need" on every cart in the corridor is noise.
   */
  const hidden = new Set();

  /**
   * The point on a target's footprint nearest the player.
   *
   * Both the distance *and* the facing test are taken against this rather than
   * against the prop's centre. Measuring one from the edge and the other from
   * the middle is the bug that makes a forty foot linen shelf usable only
   * opposite its exact centre: you are three feet from the shelf, you are
   * looking straight at it, and the game says you are facing twelve degrees off
   * because the middle of the thing is eighteen feet to your left.
   */
  function nearestPoint(target, position) {
    return {
      x: clamp(position.x, target.centre.x - target.half.x, target.centre.x + target.half.x),
      z: clamp(position.z, target.centre.z - target.half.z, target.centre.z + target.half.z),
    };
  }

  return {
    targets,
    get current() {
      return current;
    },

    /**
     * Marks a target as visible-but-refused, with the reason to show. Passing
     * null clears it. This is how "you cannot clock in yet, the shift opens at
     * one" reaches the player without the prompt vanishing and leaving them
     * wondering whether they are standing in the right place.
     */
    gate(id, reason, { hidden: away = false } = {}) {
      if (reason) gates.set(id, reason);
      else gates.delete(id);
      if (away) hidden.add(id);
      else hidden.delete(id);
    },

    update(position, forward) {
      let best = null;
      for (const target of targets) {
        if (hidden.has(target.id)) continue;
        const near = nearestPoint(target, position);
        const dx = near.x - position.x;
        const dz = near.z - position.z;
        const distance = Math.hypot(dx, dz);
        if (distance > target.reachMetres) continue;

        const length = distance;
        // Standing on top of it counts as facing it; there is no direction to
        // be off by.
        const facing = length < 0.25 ? 1 : (dx / length) * forward.x + (dz / length) * forward.z;
        if (facing < FACING_THRESHOLD) continue;

        // Facing beats proximity, so turning to look at the board picks the
        // board even while you are standing nearer the clock.
        const score = facing - distance * 0.15;
        if (!best || score > best.score) best = { target, distance, facing, score };
      }

      current = best
        ? { ...best.target, distance: best.distance, blocked: gates.get(best.target.id) ?? null }
        : null;
      return current;
    },
  };
}
